import { Box, Stack } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { memo, ReactNode } from 'react';
import { makeStyles } from 'tss-react/mui';

import {
  SELECT_CHEVRON_ICON_SX,
  SelectChevronIcon,
} from '@/components/form/baseInputs/SelectChevronIcon';
import { MenuItemSecondaryTypography } from '@/components/typography/MenuItemSecondaryTypography';
import { MenuItemTypography } from '@/components/typography/MenuItemTypography';
import { COLORS } from '@/styles/tokens/colors';

import { StyledCheckbox } from '../Checkbox/Checkbox';
import { FormControl } from '../FormControl';
import {
  BaseMultiSelectInputProps,
  CompoundMultiSelectInputOption,
  FormControlMultiSelectInputProps,
  HelpTextVariant,
  MultiSelectInputComponentOption,
  MultiSelectInputOption,
} from '../inputTypes';

interface MultiSelectProps extends FormControlMultiSelectInputProps {
  label: string;
  id?: string;
  // the presence of contextualHelp indicates that there's help text related to this input, which
  // will cause the "info" icon to show up next to the input label
  contextualHelp?: JSX.Element;
  errorMessage?: string;
  helpText?: string;
  helpTextVariant?: HelpTextVariant;
  hideLabel?: boolean;
  labelIconEnd?: ReactNode;
}

interface RenderValueProps {
  selected: string[];
  options: CompoundMultiSelectInputOption[];
}

function areEqual(prevProps: RenderValueProps, nextProps: RenderValueProps) {
  if (prevProps.selected.length !== nextProps.selected.length) {
    return false;
  }

  for (let i = 0; i < prevProps.selected.length; i++) {
    if (prevProps.selected[i] !== nextProps.selected[i]) {
      return false;
    }
  }

  // Also re-render if options length changes - this handles the case where
  // options load asynchronously after the selected values are already set
  if (prevProps.options.length !== nextProps.options.length) {
    return false;
  }

  return true;
}

function RenderValue({ selected, options }: RenderValueProps) {
  if (selected?.length) {
    return (
      <>
        {selected
          .map((s) => {
            const foundValue = options.find((option) => option?.value === s);
            if (foundValue) {
              // For component options we may not have display/abbreviation.
              // Fallback to empty string if none provided.
              // Type coercion so that TS understands potential properties.
              const abbreviation = (foundValue as MultiSelectInputOption)
                .abbreviation as string | undefined;
              const display = (foundValue as MultiSelectInputOption).display as
                | string
                | undefined;
              return abbreviation || display || '';
            }

            return '';
          })
          .join(', ')}
      </>
    );
  }
  return <>—</>;
}

const MemoizedRenderValue = memo(RenderValue, areEqual);

const useStyles = makeStyles<{ disabled: boolean }>()(
  (_theme, { disabled }) => ({
    root: {
      background: disabled ? COLORS.GRAY[50] : 'white',
      maxWidth: 'inherit',
      maxHeight: 500,
    },
    option: {
      maxWidth: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    menuPaper: {
      maxHeight: 400,
    },
  })
);

const BaseMultiSelectInput = ({
  options,
  testId,
  required,
  value: rawValue,
  disabled,
  startAdornment,
  renderValue,
  sx,
  ...props
}: BaseMultiSelectInputProps) => {
  const { classes, cx } = useStyles({ disabled: disabled ?? false });
  const mergedSx = [
    SELECT_CHEVRON_ICON_SX,
    ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
  ];

  // Normalize to array defensively — browsers can autofill the underlying native input
  // with a string, which causes MUI's multi-select to crash (expects an array).
  const value = Array.isArray(rawValue) ? rawValue : [];

  return (
    // this select is a non-optimal experience for mobile; consider using NativeSelect here for a mobile device
    <Select
      className={cx(classes.root)}
      MenuProps={{ classes: { paper: cx(classes.menuPaper) } }}
      disabled={disabled}
      displayEmpty
      IconComponent={SelectChevronIcon}
      multiple
      sx={mergedSx}
      inputProps={{ 'data-testid': testId, autoComplete: 'off' }}
      value={value}
      required={required}
      renderValue={
        renderValue ??
        ((selected) => (
          <MemoizedRenderValue selected={selected} options={options} />
        ))
      }
      startAdornment={startAdornment}
      data-testid={`multi-select-${props.name ?? ''}`}
      {...props}
    >
      {options.map((option, i) => {
        // Handle custom component option type
        if ((option as MultiSelectInputComponentOption).type === 'component') {
          const componentOption = option as MultiSelectInputComponentOption;

          // If this option has an associated value, we need to keep the checkbox behaviour.
          if (componentOption.value !== undefined) {
            return (
              <MenuItem
                key={i}
                value={componentOption.value}
                disabled={componentOption.disabled}
                className={cx(classes.option)}
              >
                <StyledCheckbox
                  disabled={componentOption.disabled}
                  value={value.includes(componentOption.value)}
                  data-testid={`checkbox-${componentOption.value}`}
                />
                <Box ml={1}>{componentOption.component}</Box>
              </MenuItem>
            );
          }

          // Non-selectable custom component (no value)
          if (componentOption.component.key != null) {
            return componentOption.component;
          }

          // Wrap to add a key if missing
          const Component = () => componentOption.component;
          return <Component key={i} />;
        }

        // Default option rendering
        const regularOption = option as MultiSelectInputOption;
        return (
          <MenuItem
            key={i}
            value={regularOption.value}
            disabled={regularOption.disabled}
            className={cx(classes.option)}
          >
            <StyledCheckbox
              disabled={regularOption.disabled}
              value={value.includes(regularOption.value)}
              indeterminate={regularOption.indeterminate}
              data-testid={`checkbox-${regularOption.value}`}
            />
            <Stack direction="column" spacing={0} ml={1}>
              <MenuItemTypography>{regularOption.display}</MenuItemTypography>
              {regularOption.secondaryText && (
                <MenuItemSecondaryTypography>
                  {regularOption.secondaryText}
                </MenuItemSecondaryTypography>
              )}
            </Stack>
          </MenuItem>
        );
      })}
    </Select>
  );
};

export const MultiSelectInput = ({
  id,
  label,
  contextualHelp,
  errorMessage,
  helpText,
  helpTextVariant,
  hideLabel,
  labelIconEnd,
  ...inputProps
}: MultiSelectProps) => {
  return (
    <FormControl<BaseMultiSelectInputProps>
      id={id}
      contextualHelp={contextualHelp}
      component={BaseMultiSelectInput}
      componentKind="multiSelect"
      label={label}
      hideLabel={hideLabel}
      helpText={helpText}
      helpTextVariant={helpTextVariant}
      required={inputProps.required}
      errorMessage={errorMessage}
      inputProps={inputProps}
      labelIconEnd={labelIconEnd}
    />
  );
};
